import { IS_DEV_ENV } from "constants/app.constants";
import { EntityType } from "constants/entities.enum";
import { createEntityState } from "entities/charts/factory";
import { createMergeWithDefaults } from "entities/utils";
import { EvaChartFeatures } from "types";

import {
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY
} from "./feature";

// Bare-bones feature set for the Rig/Frac Activity Over Time chart (EVA-76).
// Only screenshot, fullscreen, legend, and the data table (export) are on —
// every wells/facilities concern (forecast, source, product focus, cutoff,
// normalization, …) is hidden, per spec FR-008/FR-013.
// Exported so the Well Status widget can build on it: that set is this
// one plus chartFocus — see features/wellStatus.ts.
export const RIG_FRAC_ACTIVITY_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: createEntityState(EntityType.ScreenshotFeature, {
    show: true,
    enabled: true
  }),
  fullScreen: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  datatable: createEntityState(EntityType.ChartDataTableFeature, {
    show: true,
    enabled: true
  }),
  legend: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    // Shown by default (and LegendToggle reflects/flips this).
    active: true
  }),
  debug: createEntityState(EntityType.ChartsFeature, {
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  }),
  spaghetti: createEntityState(EntityType.ChartsFeature, { show: false, enabled: false }),
  forecast: createEntityState(EntityType.ChartsFeature, { show: false, enabled: false }),
  source: createEntityState(EntityType.SourceFeature, { show: false, enabled: false }),
  timeStep: createEntityState(EntityType.TimeStepFeature, {
    show: false,
    enabled: false
  }),
  isProducingRateType: createEntityState(EntityType.ChartsFeature, {
    show: false,
    enabled: false
  }),
  cutoff: createEntityState(EntityType.CutOffChartFeature, {
    show: false,
    enabled: false
  }),
  shutInMonths: createEntityState(EntityType.ShutInMonthsChartFeature, {
    show: false,
    enabled: false
  }),
  movingAverageDays: createEntityState(EntityType.MovingAverageDaysChartFeature, {
    show: false,
    enabled: false
  }),
  survivorBias: createEntityState(EntityType.ChartsFeature, {
    show: false,
    enabled: false
  }),
  useWeightedRatioAverage: createEntityState(EntityType.ChartsFeature, {
    show: false,
    enabled: false
  }),
  lockUnits: createEntityState(EntityType.ChartsFeature, { show: false, enabled: false }),
  lasso: createEntityState(EntityType.LassoFeature, { show: false, enabled: false }),
  typewells: createEntityState(EntityType.ChartsFeature, { show: false, enabled: false }),
  p10: createEntityState(EntityType.P10Feature, { show: false, enabled: false }),
  p50: createEntityState(EntityType.P50Feature, { show: false, enabled: false }),
  p90: createEntityState(EntityType.P90Feature, { show: false, enabled: false }),
  average: createEntityState(EntityType.AverageFeature, {
    show: false,
    enabled: false
  }),
  normalization: createEntityState(EntityType.NormalizationFeature, { show: false }),
  chartFocus: createEntityState(EntityType.ChartFocusFeature, {
    show: false,
    enabled: false
  }),
  multiProductFocus: createEntityState(EntityType.MultiProductFocusFeature, {
    show: false,
    enabled: false
  }),
  showWellCount: createEntityState(EntityType.ChartsFeature, {
    show: false,
    enabled: false
  }),
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointSize: {
    ...DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointOpacity: {
    ...DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  useSeriesStyleOverride: createEntityState(EntityType.ChartsFeature, {
    show: false,
    enabled: false,
    active: false
  }),
  logAxis: createEntityState(EntityType.LogAxisFeature, {
    properties: {
      x: { active: false },
      y: { active: false }
    }
  }),
  bestFitToggle: createEntityState(EntityType.BestFitToggleFeature, {}),
  histogramToggle: createEntityState(EntityType.HistogramToggleFeature, {}),
  jitterPlotToggle: createEntityState(EntityType.JitterPlotToggleFeature, {}),
  quarterSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  unitSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  halfSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  useProbitLineOfBestFitData: createEntityState(EntityType.ChartsFeature, {}),
  chartTypeParam: createEntityState(EntityType.ChartTypeParamFeature, {})
};

export const rigFracActivityFeatures = createMergeWithDefaults(
  RIG_FRAC_ACTIVITY_FEATURES
);
